import { useEffect, useMemo } from 'react';
import type { CSSProperties, RefObject } from 'react';
import type { JSONContent } from '@tiptap/core';
import { EditorContent } from '@tiptap/react';
import { sanitizeRichTextContent } from '@course-authoring/shared-types';

import { getInteractionRichTextFieldPresetConfig } from './interactionEditorUtils';
import { createRichTextEditorExtensions } from './richTextEditorExtensions';
import { RichTextToolbar } from './richTextToolbar';
import { useRichTextFieldEditor } from './useRichTextFieldEditor';
import type {
  InteractionRichTextToolbarPreset,
  InteractionRichTextToolbarStyles,
} from './interactionEditorUtils';

interface InteractionRichTextFieldProps {
  content: JSONContent;
  placeholder: string;
  isFocused: boolean;
  onFocus: () => void;
  onBlur: () => void;
  onContentChange: (content: JSONContent) => void;
  autoFocus: boolean;
  containerRef: RefObject<HTMLElement | null>;
  editorClassName?: string;
  editorInlineStyle?: string;
  editorStyle?: CSSProperties;
  toolbarPreset: InteractionRichTextToolbarPreset;
  toolbarStyles: InteractionRichTextToolbarStyles;
  shouldRerenderOnTransaction?: boolean;
  backgroundColors?: string[];
  hideToolbar?: boolean;
  defaultBold?: boolean;
}

export function InteractionRichTextField({
  content,
  placeholder,
  isFocused,
  onFocus,
  onBlur,
  onContentChange,
  autoFocus,
  containerRef,
  editorClassName,
  editorInlineStyle = 'outline: none; min-height: 24px;',
  editorStyle,
  toolbarPreset,
  toolbarStyles,
  shouldRerenderOnTransaction = false,
  backgroundColors,
  hideToolbar = false,
  defaultBold = false,
}: InteractionRichTextFieldProps) {
  const sanitizedContent = useMemo(() => sanitizeRichTextContent(content), [content]);
  const presetConfig = useMemo(
    () => getInteractionRichTextFieldPresetConfig(toolbarPreset, backgroundColors),
    [toolbarPreset, backgroundColors]
  );
  const extensions = useMemo(
    () => createRichTextEditorExtensions(placeholder, presetConfig.extensionOptions),
    [placeholder, presetConfig.extensionOptions]
  );

  const editor = useRichTextFieldEditor({
    extensions,
    content: sanitizedContent,
    onContentChange,
    onFocus,
    onBlur,
    containerRef,
    editorClassName,
    editorInlineStyle,
    autoFocus,
    shouldRerenderOnTransaction,
  });

  useEffect(() => {
    if (!editor || !defaultBold) return;
    const boldMark = editor.schema.marks.bold;
    if (!boldMark) return;

    const applyStoredBold = () => {
      if (!editor.isEmpty) return;
      const { tr } = editor.view.state;
      const currentMarks = editor.view.state.storedMarks;
      const hasBold = currentMarks?.some((mark) => mark.type === boldMark) ?? false;
      if (hasBold) return;
      editor.view.dispatch(tr.setStoredMarks([boldMark.create()]));
    };

    applyStoredBold();
    editor.on('create', applyStoredBold);
    editor.on('focus', applyStoredBold);

    return () => {
      editor.off('create', applyStoredBold);
      editor.off('focus', applyStoredBold);
    };
  }, [editor, defaultBold]);

  if (!editor) {
    return <div style={editorStyle} />;
  }

  const mergedForceActiveToolbarActions =
    defaultBold && editor.isEmpty ? { bold: true } : undefined;

  const toolbarOpacityStyle: CSSProperties = isFocused
    ? {}
    : { opacity: 0.5, pointerEvents: 'none' as const };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {!hideToolbar ? (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ ...toolbarOpacityStyle, flex: 1 }}>
          <RichTextToolbar
            editor={editor}
            items={presetConfig.toolbarItems}
            toolbarStyle={toolbarStyles.toolbar}
            buttonStyle={toolbarStyles.button}
            activeButtonStyle={toolbarStyles.activeButton}
            dividerStyle={toolbarStyles.divider}
            forceActive={mergedForceActiveToolbarActions}
          />
        </div>
      </div>
      ) : null}
      <EditorContent editor={editor} style={editorStyle} />
    </div>
  );
}
